import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Bus Sales Service
 * Otobüs satış API'leri için servis
 */
export const busSalesService = {
  /**
   * Origin ve destination bilgilerini getir
   * @param {string} urlFromToData - URL'den gelen origin-destination slug'ı (örn: "istanbul-avrupa-bolgesi-ankara-asti-otogari")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * getOriginAndDestination("istanbul-avrupa-bolgesi-ankara-asti-otogari")
   * → POST /v1/bus/sales/GetOriginAndDestination
   * Body: { "UrlFromToData": "istanbul-avrupa-bolgesi-ankara-asti-otogari" }
   * 
   * Response:
   * {
   *   "Origin": {
   *     "BusTerminalId": 774,
   *     "Name": "İstanbul Avrupa",
   *     "Slug": "istanbul-avrupa-bolgesi",
   *     "CityCode": 2346,
   *     "LocalizedCityName": "İstanbul"
   *   },
   *   "Destination": {
   *     "BusTerminalId": 542,
   *     "Name": "Ankara (Aşti)",
   *     "Slug": "ankara-asti-otogari",
   *     "CityCode": 2347,
   *     "LocalizedCityName": "Ankara"
   *   }
   * }
   */
  getOriginAndDestination: (urlFromToData = "", headers = {}, hasErrorHandler = false) => {
    const payload = {
      UrlFromToData: urlFromToData,
    };
    
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.getOriginAndDestination,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Otobüs arama v2 - Gelişmiş arama ve filtreleme
   * @param {Object} payload - Tam payload objesi (dinamik olarak dışarıdan gönderilir)
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * searchv2({
   *   ClientChannel: "Saka",
   *   Origin: "rize",
   *   Destination: "istanbul-avrupa",
   *   IsRT: false,
   *   DepartureDate: "2025-10-25",
   *   ActualScrollIndex: 0,
   *   CurrencyCode: "TRY",
   *   LanguageCode: "tr",
   *   Paxes: [{ PaxType: "ADT", Count: 1 }],
   *   SelectedSorting: { Ascending: true, SortBy: "Default" },
   *   SelectedFilters: {}
   * })
   * → POST /v1/bus/sales/searchv2
   */
  searchv2: (payload = {}, headers = {}, hasErrorHandler = false) => {
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.searchv2,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Sefer koltuk ve detaylarını getir (Trip Details V2)
   * @param {Object} payload - İstek gövdesi
   * @param {string} payload.SearchReference - Arama referansı (SearchV2 dönüşünden gelir)
   * @param {string} payload.TripReference - Sefer referansı (SearchV2 sonuç item'ından gelir)
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * busTripDetailsV2({
   *   SearchReference: "...",
   *   TripReference: "...",
   *   LanguageCode: "tr"
   * })
   * → POST /v1/bus/bustripdetailsV2
   * 
   * Response (özet):
   * {
   *   "TripDetails": {
   *     "Reference": "...",
   *     "Details": {
   *       "DepartureTime": "2025-11-27T19:15:00.0000000",
   *       "FirstStopLocation": { "Id": 1224, "Name": "Rize" },
   *       "LastStopLocation": { "Id": 1652, "Name": "Halıcıoğlu", "City": "İstanbul" },
   *       "BusCompany": { "Id": 352, "Name": "Lüks Karadeniz Seyahat", "LogoUrl": "..." },
   *       "Vehicle": { "SeatingType": "tourismo2+1 41li", "GeneralAmenitiesList": [...] },
   *       "Stopovers": [ { "Name": "Rize", "Time": "..." }, ... ],
   *       "PaxPrices": [ { "PaxType": 0, "Total": 1400.00, "Currency": "TRY" } ],
   *       "AvailableSeats": 14,
   *       "BookingPolicy": { "IsBookingEnabled": true, ... },
   *       "CancellationPolicy": { "IsAllowed": true, ... },
   *       "IsDirect": false
   *     }
   *   },
   *   "SeatBlocksList": [
   *     { "FloorNumber": 1, "Number": 1, "BlockType": 3, "GenderAvailable": 3, "Reference": "...", "Price": 1400.00 },
   *     ...
   *   ]
   * }
   */
  busTripDetailsV2: (payload = {}, headers = {}, hasErrorHandler = false) => {
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.busTripDetailsV2,
      payload,
      headers,
      hasErrorHandler,
    });
  },



  /**
   * Sefer koltuk ve detaylarını getir (Trip Details V2)
   * @param {Object} payload - İstek gövdesi
   * @param {string} payload.SearchReference - Arama referansı (SearchV2 dönüşünden gelir)
   * @param {string} payload.TripReference - Sefer referansı (SearchV2 sonuç item'ından gelir)
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * busTripDetailsV2({
   *   SearchReference: "...",
   *   TripReferenceList: ["...", "..."],
   *   LanguageCode: "tr"
   * })
   * → POST /v1/bus/sales/bustripdetailssmarttrip
   * */

  busTripDetailsSmarttrip: (payload = {}, headers = {}, hasErrorHandler = false) => {
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.busTripDetailsSmarttrip,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Koltuk tahsisi yap (Allocate)
   * @param {Object} payload - İstek gövdesi
   * @param {string} payload.SearchReference - Arama referansı
   * @param {string} payload.TripReference - Sefer referansı
   * @param {string} payload.IsInNextNight - Gece yarısı sonrası mı (string)
   * @param {Array} payload.SelectedSeats - Seçilen koltuklar
   * @param {number} payload.SelectedSeats[].SeatNumber - Koltuk numarası
   * @param {string} payload.SelectedSeats[].Reference - Koltuk referansı
   * @param {string} payload.SelectedSeats[].PassengerGender - Yolcu cinsiyeti ("Male" veya "Female")
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {number} payload.AppVersion - Uygulama versiyonu
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * allocate({
   *   SearchReference: "...",
   *   TripReference: "...",
   *   IsInNextNight: "false",
   *   SelectedSeats: [
   *     {
   *       SeatNumber: 1,
   *       Reference: "...",
   *       PassengerGender: "Male"
   *     }
   *   ],
   *   LanguageCode: "tr",
   *   AppVersion: 0
   * })
   * → POST /v1/bus/sales/allocate
   * 
   * Response:
   * {
   *   "BasketId": 123,
   *   "IsHesCodeRequired": true,
   *   "IsPrePaymentEnabled": true,
   *   "Products": [...]
   * }
   */
  allocate: (payload = {}, headers = {}, hasErrorHandler = false) => {
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.allocate,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Seçilen koltuklar için fiyat hesapla (CalculateSeatFares)
   * @param {Array<Object>} payload - İstek gövdesi (sefer sepetleri listesi)
   * @param {number} payload[].Total - Toplam tutar
   * @param {number} payload[].Service - Hizmet bedeli
   * @param {number} payload[].DiscountedTotal - İndirimli toplam tutar
   * @param {Array<number>} payload[].SeatPrices - Koltuk bazlı fiyatlar
   * @param {Array<{PaxType: string, Count: number}>} payload[].SeatPaxes - Yolcu tip ve adet bilgileri
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Örnek istek:
   * [
   *   {
   *     "Total": 1815.0,
   *     "Service": 165.0,
   *     "DiscountedTotal": 1815.0,
   *     "SeatPrices": [605.0, 605.0, 605.0],
   *     "SeatPaxes": [{ "PaxType": "ADT", "Count": 3 }]
   *   },
   *   {
   *     "Total": 5907.0,
   *     "Service": 537.0,
   *     "DiscountedTotal": 5907.0,
   *     "SeatPrices": [2640.0, 2640.0, 2640.0],
   *     "SeatPaxes": [{ "PaxType": "ADT", "Count": 3 }]
   *   }
   * ]
   *
   * → POST /v1/bus/sales/calculateseatfares
   */
  calculateSeatFares: (payload = [], headers = {}, hasErrorHandler = false) => {
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.calculateSeatFares,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Prebook oluştur (MakePrebook)
   * @param {Object} payload - İstek gövdesi
   * @param {number} payload.BasketId - Sepet ID'si
   * @param {string} payload.BasketState - Sepet durumu (örn: "NotFinalized")
   * @param {Array} payload.Passengers - Yolcu listesi
   * @param {number} payload.Passengers[].Id - Yolcu ID'si
   * @param {number} payload.Passengers[].SequenceNo - Yolcu sırası
   * @param {number} payload.Passengers[].BasketId - Yolcu sepet ID'si
   * @param {string} payload.Passengers[].Type - Yolcu tipi (örn: "ADT")
   * @param {string} payload.Passengers[].Gender - Yolcu cinsiyeti ("Male" | "Female")
   * @param {string} payload.Passengers[].FirstName - Ad
   * @param {string} payload.Passengers[].LastName - Soyad
   * @param {string} payload.Passengers[].MiddleName - İkinci ad
   * @param {string} payload.Passengers[].BirthDate - ISO tarih formatında doğum tarihi
   * @param {string} payload.Passengers[].PassportNo - Pasaport numarası
   * @param {string} payload.Passengers[].PassportValidDate - Pasaport geçerlilik tarihi
   * @param {string} payload.Passengers[].CitizenNo - Vatandaşlık numarası
   * @param {string} payload.Passengers[].Nationality - Uyruk
   * @param {string} payload.Passengers[].MobilePhoneNumber - Telefon numarası
   * @param {string} payload.Passengers[].Email - E-posta
   * @param {boolean} payload.Passengers[].IsContact - İletişim kişisi mi
   * @param {string} payload.Passengers[].IdentityNo - Kimlik numarası
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Usage Example:
   * makePrebook({
   *   BasketId: 123,
   *   BasketState: "NotFinalized",
   *   Passengers: [
   *     {
   *       Id: 0,
   *       SequenceNo: 0,
   *       BasketId: 123,
   *       Type: "ADT",
   *       Gender: "Male",
   *       FirstName: "Ahmet",
   *       LastName: "Yılmaz",
   *       BirthDate: "2025-11-20T05:48:47.769Z",
   *       PassportNo: "string",
   *       PassportValidDate: "2025-11-20T05:48:47.769Z",
   *       CitizenNo: "string",
   *       Nationality: "string",
   *       MobilePhoneNumber: "string",
   *       Email: "string",
   *       IsContact: true,
   *       IdentityNo: "string"
   *     }
   *   ]
   * })
   * → POST /v1/bus/sales/makeprebook
   */
  makePrebook: (payload = {}, headers = {}, hasErrorHandler = false) => {
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.makePrebook,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Sepet bilgilerini getir (Get Basket)
   * @param {Object} payload - İstek gövdesi
   * @param {number} payload.BasketId - Sepet ID'si
   * @param {Object} [payload.BookingCode] - Rezervasyon kodu (opsiyonel)
   * @param {string} [payload.BookingCode.Code] - Rezervasyon kodu
   * @param {string} [payload.BookingCode.LastName] - Soyadı
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {number} payload.AppVersion - Uygulama versiyonu
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * getBasket({
   *   BasketId: 123,
   *   LanguageCode: "tr",
   *   AppVersion: 0
   * })
   * → POST /v1/bus/sales/getbasket
   * 
   * Usage Example with BookingCode:
   * getBasket({
   *   BasketId: 123
   * })
   * → POST /v2/flight/ticketing/GetActiveBasket
   */
  getBasket: (payload = {}, headers = {}, hasErrorHandler = false) => {
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.getBasket,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Otobüs rezervasyonunu iptal et
   * @param {Object} payload - İstek gövdesi
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {number} payload.BasketId - Sepet ID'si
   * @param {number[]} payload.TicketIdList - İptal edilecek bilet ID'leri
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Usage Example:
   * cancelBusBook({
   *   LanguageCode: "tr",
   *   BasketId: 128597,
   *   TicketIdList: [58975, 15465]
   * })
   * → POST /v1/bus/sales/CancelBusBook
   */
  cancelBusBook: (payload = {}, headers = {}, hasErrorHandler = false) => {
    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.bus.sales.cancelBusBook,
      payload,
      headers,
      hasErrorHandler,
    });
  },
};
